import { Trip } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";


import {
  averageConsumptionKgPer100Km,
  isStaleReading,
  tripDistanceKm,
} from "../models/fuelCellMetrics";
import {
  FuelCellRow,
  FuelCellSample,
  FuelCellSummaryStats,
  LOW_FUEL_THRESHOLD_PERCENT,
} from "../models/FuelCellRow";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { DIAGNOSTIC_FUEL_LEVEL_ID, FUEL_CELL_GROUP_NAME } from "@/constants/constants";
import { useGroupByName } from "@/hooks/groups/useGroupByName";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";


const HISTORY_RESULTS_LIMIT = 50000;
const TRIP_RESULTS_LIMIT = 50000;
const REFILL_THRESHOLD_PERCENT = 10;
const NOISE_THRESHOLD_PERCENT = 0.5;

type FuelTankCapacityValue =
  | number
  | { volume?: number | null; source?: unknown }
  | null
  | undefined;

type DeviceWithFuelTank = DeviceWithVehicleInfo & {
  fuelTankCapacity?: FuelTankCapacityValue;
};

interface DeviceHistory {
  deviceId: string;
  samples: FuelCellSample[];
  truncated: boolean;
}

function getTankCapacityKg(device: DeviceWithFuelTank): number | null {
  const capacity = device.fuelTankCapacity;
  if (typeof capacity === "number" && Number.isFinite(capacity)) {
    return capacity;
  }
  if (
    capacity &&
    typeof capacity === "object" &&
    typeof capacity.volume === "number" &&
    Number.isFinite(capacity.volume)
  ) {
    return capacity.volume;
  }
  return null;
}

function sortSamples(samples: FuelCellSample[]): FuelCellSample[] {
  return [...samples].sort(
    (a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime(),
  );
}

function summarizeHistory(sorted: FuelCellSample[]): {
  usedPercent: number;
  refillCount: number;
} | null {
  if (sorted.length < 2) {
    return null;
  }
  let usedPercent = 0;
  let refillCount = 0;
  for (let i = 1; i < sorted.length; i++) {
    const delta = sorted[i].data - sorted[i - 1].data;
    if (delta >= REFILL_THRESHOLD_PERCENT) {
      refillCount++;
    } else if (delta < -NOISE_THRESHOLD_PERCENT) {
      usedPercent += -delta;
    }
  }
  return { usedPercent, refillCount };
}

/**
 * The fuel-cell fleet over a period.
 *
 * @param fromDate - Start of the period.
 * @param toDate - End of the period.
 * @param minDistanceKm - Distance below which no average consumption is reported, see
 * {@link averageConsumptionKgPer100Km}.
 */
export function useFuelCellData(fromDate: Date, toDate: Date, minDistanceKm: number) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const {
    data: group,
    isLoading: isLoadingGroup,
    error: groupError,
  } = useGroupByName(FUEL_CELL_GROUP_NAME);

  const groupId = group?.id ?? null;

  const {
    data: devices = [],
    isLoading: isLoadingDevices,
    isFetching: isFetchingDevices,
    error: devicesError,
  } = useQuery<DeviceWithFuelTank[]>({
    queryKey: ["fuelCellDevices", activeSessionId, groupId],
    enabled: !!groupId,
    queryFn: async ({ signal }) => {
      if (!groupId) return [];
      const result = await geotabClient.call(
        "Get",
        {
          typeName: "Device",
          search: {
            fromDate: new Date(),
            groupFilterCondition: { groupId },
          },
          propertySelector: {
            fields: [
              "id",
              "name",
              "licenseplate",
              "serialNumber",
              "vehicleIdentificationNumber",
              "fuelTankCapacity",
              "groups",
            ],
            isIncluded: true,
          },
          resultsLimit: 1000,
        },
        signal,
      );
      return (result as DeviceWithFuelTank[] | null) ?? [];
    },
    refetchInterval: 60000,
    placeholderData: keepPreviousData,
  });

  const deviceIds = useMemo(() => devices.map(d => d.id), [devices]);

  const {
    data: statusInfos = {},
    isLoading: isLoadingStatus,
    isFetching: isFetchingStatus,
    error: statusError,
  } = useStatusInfosByIds(deviceIds, [DIAGNOSTIC_FUEL_LEVEL_ID]);

  const fromIso = useMemo(() => fromDate.toISOString(), [fromDate]);
  const toIso = useMemo(() => toDate.toISOString(), [toDate]);

  const {
    data: histories = [],
    isLoading: isLoadingHistory,
    isFetching: isFetchingHistory,
    error: historyError,
  } = useQuery<DeviceHistory[]>({
    queryKey: ["fuelCellHistory", activeSessionId, deviceIds, fromIso, toIso],
    enabled: deviceIds.length > 0,
    queryFn: async ({ signal }) => {
      const results = await Promise.all(
        deviceIds.map(async deviceId => {
          const samples = (await geotabClient.get(
            "StatusData",
            {
              fromDate,
              toDate,
              diagnosticSearch: { id: DIAGNOSTIC_FUEL_LEVEL_ID },
              deviceSearch: { id: deviceId },
            },
            HISTORY_RESULTS_LIMIT,
            signal,
          )) as FuelCellSample[];
          return {
            deviceId,
            samples: samples ?? [],
            truncated: (samples?.length ?? 0) >= HISTORY_RESULTS_LIMIT,
          };
        }),
      );
      return results;
    },
    placeholderData: keepPreviousData,
  });

  const historyByDevice = useMemo(() => {
    const map = new Map<string, { samples: FuelCellSample[]; truncated: boolean }>();
    for (const h of histories) {
      map.set(h.deviceId, { samples: sortSamples(h.samples), truncated: h.truncated });
    }
    return map;
  }, [histories]);

  const samplesByDevice = useMemo(() => {
    const map = new Map<string, FuelCellSample[]>();
    for (const [id, h] of historyByDevice) map.set(id, h.samples);
    return map;
  }, [historyByDevice]);

  // The distance comes off the trips. Reading it from the odometer at each end of the period,
  // which is what this page did, needs a reading to exist at both instants and reports nothing at
  // all where one is missing — or where the later one reads lower after a device swap.
  const {
    data: trips,
    isLoading: isLoadingTrips,
    isFetching: isFetchingTrips,
    error: tripsError,
  } = useQuery({
    queryKey: ["fuelCellTrips", activeSessionId, deviceIds, fromIso, toIso],
    enabled: deviceIds.length > 0,
    queryFn: async ({ signal }) => {
      const results = await Promise.all(
        deviceIds.map(async deviceId => {
          const deviceTrips = await geotabClient.get(
            "Trip",
            { deviceSearch: { id: deviceId }, fromDate, toDate },
            TRIP_RESULTS_LIMIT,
            signal,
          );
          return {
            deviceId,
            trips: deviceTrips ?? [],
            // Geotab cuts a result set off at the limit and says nothing about having done so,
            // so the count is the only signal there is. Reported the same way a truncated
            // history already is.
            truncated: (deviceTrips?.length ?? 0) >= TRIP_RESULTS_LIMIT,
          };
        }),
      );
      return results;
    },
    placeholderData: keepPreviousData,
  });

  const tripsByDevice = useMemo(() => {
    const map = new Map<string, { trips: Trip[]; truncated: boolean }>();
    for (const entry of trips ?? []) {
      map.set(entry.deviceId, { trips: entry.trips, truncated: entry.truncated });
    }
    return map;
  }, [trips]);

  const rows: FuelCellRow[] = useMemo(
    () =>
      devices.map(device => {
        const tankCapacityKg = getTankCapacityKg(device);

        const history = historyByDevice.get(device.id);

        // The last level reported inside the period, not the level right now. The whole row
        // describes the period, and a "now" figure next to per-period figures reads as one of
        // them. The live status is the fallback for a vehicle that reported nothing in between.
        const latestSample = history?.samples.at(-1);
        const liveEntry = statusInfos[device.id]?.statusData?.find(
          entry => entry.diagnostic?.id === DIAGNOSTIC_FUEL_LEVEL_ID,
        );
        const rawLevel = latestSample?.data ?? liveEntry?.data;
        const levelPercent =
          typeof rawLevel === "number" && Number.isFinite(rawLevel) ? rawLevel : null;
        const reportedAt = latestSample?.dateTime ?? liveEntry?.dateTime;
        const updatedAt = reportedAt ? new Date(reportedAt) : null;

        const remainingKg =
          levelPercent != null && tankCapacityKg != null
            ? (levelPercent / 100) * tankCapacityKg
            : null;
        const usedKg =
          levelPercent != null && tankCapacityKg != null
            ? ((100 - levelPercent) / 100) * tankCapacityKg
            : null;

        const summary = history ? summarizeHistory(history.samples) : null;
        const usedRangeKg =
          summary && tankCapacityKg != null
            ? (summary.usedPercent / 100) * tankCapacityKg
            : null;
        const refillCount = summary ? summary.refillCount : null;

        const deviceTrips = tripsByDevice.get(device.id);
        const distanceKm = tripDistanceKm(deviceTrips?.trips);
        const avgConsumptionKgPer100Km = averageConsumptionKgPer100Km(
          usedRangeKg,
          distanceKm,
          minDistanceKm,
        );

        return {
          id: device.id,
          name: device.name ?? "",
          licensePlate: device.licensePlate ?? "",
          vin: device.vehicleIdentificationNumber ?? "",
          serialNumber: device.serialNumber ?? "",
          tankCapacityKg,
          levelPercent,
          remainingKg,
          usedKg,
          updatedAt,
          isStale: isStaleReading(distanceKm, latestSample != null),
          usedRangeKg,
          refillCount,
          distanceKm,
          avgConsumptionKgPer100Km,
          rangeTruncated: (history?.truncated ?? false) || (deviceTrips?.truncated ?? false),
        };
      }),
    [devices, statusInfos, historyByDevice, tripsByDevice, minDistanceKm],
  );

  const truncatedDeviceNames = useMemo(
    () => rows.filter(r => r.rangeTruncated).map(r => r.name || r.licensePlate || r.id),
    [rows],
  );

  const summaryStats: FuelCellSummaryStats = useMemo(() => {
    const totalUsedKg = rows.reduce((sum, r) => sum + (r.usedRangeKg ?? 0), 0);
    const totalRefills = rows.reduce((sum, r) => sum + (r.refillCount ?? 0), 0);
    const levels = rows.map(r => r.levelPercent).filter((v): v is number => v != null);
    const avgLevelPercent =
      levels.length > 0 ? levels.reduce((s, v) => s + v, 0) / levels.length : null;
    const lowFuelCount = rows.filter(
      r => r.levelPercent != null && r.levelPercent < LOW_FUEL_THRESHOLD_PERCENT,
    ).length;
    return {
      totalUsedKg,
      totalRefills,
      avgLevelPercent,
      lowFuelCount,
      deviceCount: rows.length,
    };
  }, [rows]);

  const groupMissing = !isLoadingGroup && !groupError && group === null;

  return {
    rows,
    samplesByDevice,
    isLoading:
      isLoadingGroup ||
      isLoadingDevices ||
      (deviceIds.length > 0 &&
        (isLoadingStatus ||
          isLoadingHistory ||
          isLoadingTrips)),
    isFetching:
      isFetchingDevices ||
      isFetchingStatus ||
      isFetchingHistory ||
      isFetchingTrips,
    error:
      (groupError as Error | null) ??
      (devicesError as Error | null) ??
      (statusError as Error | null) ??
      (historyError as Error | null) ??
      (tripsError as Error | null) ??
      null,
    groupMissing,
    truncatedDeviceNames,
    summaryStats,
  };
}
